import React, { useMemo, useState } from "react";
import { useNotifications } from "../components/NotificationCenter";
import { supabase } from "../Supabase";

const SUPPORT_EMAIL = "contact@invoiceu.co.uk";

const RequestFeaturePage = ({ user, onBack }) => {
  const { notify } = useNotifications();
  const [subject, setSubject] = useState("Feature request for InvoiceU");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  const canSubmit = useMemo(() => details.trim().length > 0, [details]);

  const handleSend = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      notify({
        type: "warning",
        title: "Add a short description",
        message: "Tell us what you need before sending.",
      });
      return;
    }

    try {
      setSending(true);
      const { error } = await supabase.functions.invoke(
        "send-feature-request",
        {
          body: {
            subject: subject.trim() || "Feature request for InvoiceU",
            details,
            fromEmail: user?.email || null,
          },
        }
      );

      if (error) {
        throw error;
      }

      setDetails("");
      notify({
        type: "success",
        title: "Request sent",
        message: "Thanks! We'll review your idea and get back to you.",
      });
    } catch (e) {
      notify({
        type: "error",
        title: "Unable to send",
        message:
          e?.message ||
          `Please try again or email us directly at ${SUPPORT_EMAIL}.`,
      });
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
    } else if (typeof window !== "undefined") {
      window.history.back();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Request a Feature
          </h1>
          <p className="text-sm text-slate-500">
            Tell us what would make InvoiceU better for your workflow.
          </p>
        </div>
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center justify-center rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          Back to profile
        </button>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <form className="space-y-4" onSubmit={handleSend}>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-700">
              Your email
            </label>
            <input
              type="text"
              value={user?.email || "Not signed in"}
              readOnly
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-700">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              maxLength={140}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-700">
              What would you like us to build?
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="min-h-[140px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder="Describe the workflow, the problem you're solving, and any must-have details."
              maxLength={1500}
            />
            <p className="text-[11px] text-slate-400">
              This sends straight to our inbox. We reply from contact@invoiceu.co.uk.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={!canSubmit || sending}
              className={[
                "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold",
                canSubmit && !sending
                  ? "bg-black text-white hover:bg-slate-800"
                  : "cursor-not-allowed bg-slate-200 text-slate-500",
              ].join(" ")}
            >
              {sending ? "Sending..." : "Send request"}
            </button>
            <a
              className="text-sm font-medium text-slate-700 underline"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              Or email us at {SUPPORT_EMAIL}
            </a>
          </div>
        </form>
      </section>
    </div>
  );
};

export default RequestFeaturePage;
